/** The reads the More destinations and Profile draw first, gated exactly as each
 *  screen is, so the background warm fills only what this person can open and
 *  never buys a 403. Each read is named once in its feature and shared with its
 *  screen; every one is a first page or a summary, never a whole register. */

import { DataService } from '../services/DataService';
import { currentBackgroundReadScope, readInBackground, type BackgroundReadScope } from '../services/BackgroundReadCache';
import { CalendarFeedInvalidatedError, loadCalendarFeed } from '../services/CalendarFeedCache';
import type { WarmTask } from '../services/mainScreenWarm';
import {
  canOpenEmail,
  canOpenFinanceHubs,
  canOpenOutreach,
  canOpenWorkspaceDirectory,
  navigationGranted,
  type ScreenAccessAuth,
} from '../platform/navigation/mainScreenAccess';
import { isAdministratorRole } from '../utils/permissions';
import type { UserRole } from '../types/auth';
import {pendingBatchesRead, templatesRead, settingsRead, suppressionsRead, loadOutreachRead} from '../features/outreach/outreachReads';
import { allProspectsFirstPageRead } from '../features/outreach/prospectReads';
import { warmInvoiceRegister } from '../features/invoices/invoiceRegisterReads';
import { payrollOverviewRead } from '../features/payroll/payrollReads';
import { warmLastSharedInbox } from '../features/email/sharedInboxReads';
import { workspaceCreditColumnsRead } from '../features/workspaces/workspaceCreditReads';
import { openingCalendarEventsFeed } from '../features/calendar/calendarRange';
import { workerRecordRead } from '../features/profile/useWorkerRecord';
import { fieldAccessRead } from '../features/profile/fieldAccessRead';

/** These screens refresh what they show when they open, so a warm only needs a
 *  recent first paint: half an hour holds each one to two edge calls an hour. */
export const MORE_REWARM_AFTER_MS = 30 * 60_000;

export interface MoreWarmAuth extends ScreenAccessAuth {
  activeWorkspaceId?: string | null;
  user?: { id?: string; role?: string; workspaceId?: string | null; workspace_id?: string | null } | null;
}

export type MoreWarmNavigation = Parameters<typeof navigationGranted>[0];

/** The workspace each read uses for this person, or none when its screen would not open. */
export interface MoreReadWorkspaces {
  outreach?: string;
  salesInvoices?: string;
  purchases?: string;
  payroll?: string;
  sharedInbox?: string;
  workspaceDirectory?: string;
  calendar?: string;
  profile?: string;
}

export function moreReadWorkspaces(auth: MoreWarmAuth, navigation: MoreWarmNavigation): MoreReadWorkspaces {
  // These screens read the user's own workspace, not the active one; Calendar and Profile read the active one.
  const own = auth.user?.workspaceId ?? auth.user?.workspace_id ?? undefined;
  const active = auth.activeWorkspaceId ?? undefined;
  const granted = (destination: string) => navigationGranted(navigation, destination);
  const finance = canOpenFinanceHubs(auth);
  return {
    outreach: canOpenOutreach(auth) && granted('outreach') ? own : undefined,
    // A super admin's Sales opens on platform billing, not the invoice register.
    salesInvoices: finance && granted('sales') && !isAdministratorRole(auth.user?.role as UserRole | undefined) ? own : undefined,
    purchases: finance && granted('purchases') ? own : undefined,
    payroll: finance && granted('payroll') ? own : undefined,
    sharedInbox: canOpenEmail(auth) && granted('email') ? own : undefined,
    // The directory spans every workspace; the user's own only scopes its cache entry.
    workspaceDirectory: canOpenWorkspaceDirectory(auth) && granted('workspaces') ? own : undefined,
    calendar: granted('calendar') ? active : undefined,
    profile: active,
  };
}

// A read that fails is not warmed again this session, so a screen the server
// refuses this person costs at most one call; the screen still reads for itself.
const failed = new Set<string>();
if (typeof window !== 'undefined') window.addEventListener('quantis:auth-cleared', () => failed.clear());

/** Cheap table reads first, then the edge calls (~1.5 s each), in the order people reach them. */
export function moreWarmTasks(userId: string, reads: MoreReadWorkspaces): WarmTask[] {
  const tasks: WarmTask[] = [];
  const scoped = (label: string, workspaceId: string | undefined, run: (scope: BackgroundReadScope, workspaceId: string) => Promise<unknown>) => {
    const scope = currentBackgroundReadScope(workspaceId);
    if (!scope || !workspaceId || scope.actorId !== userId) return;
    const key = JSON.stringify([userId, workspaceId, label]);
    if (failed.has(key)) return;
    tasks.push(() => run(scope, workspaceId).catch((error: unknown) => { failed.add(key); throw error; }));
  };
  const read = <T,>(workspaceId: string | undefined, named: (workspaceId: string) => { name: string; load: () => Promise<T> }, persist = true) => {
    const name = workspaceId ? named(workspaceId).name : '';
    scoped(name, workspaceId, (scope, id) => readInBackground(scope, name, named(id).load, { maxAgeMs: MORE_REWARM_AFTER_MS, persist }));
  };

  const outreachRead = (named: (workspaceId: string) => {name: string; load: () => Promise<unknown>}) => {
    const name = reads.outreach ? named(reads.outreach).name : '';
    scoped(name, reads.outreach, (_scope, id) => loadOutreachRead<unknown>(id, named(id), {maxAgeMs: MORE_REWARM_AFTER_MS}));
  };
  // Prepare the Outreach landing page before the less immediate More destinations.
  outreachRead(pendingBatchesRead);

  scoped('calendar/opening', reads.calendar, (_scope, id) => {
    // Two table reads, no edge call. The feed cache keeps the calendar's own
    // five-minute freshness, so this re-reads on each warm rather than half-hourly.
    // A feed the page invalidated mid-read is not a failure.
    const feed = openingCalendarEventsFeed(userId, id);
    return loadCalendarFeed(feed.scope, 'events', feed.load).catch((error: unknown) => {
      if (!(error instanceof CalendarFeedInvalidatedError)) throw error;
    });
  });
  // Pay and bank details: held in memory, never written to the device.
  read(reads.profile, () => workerRecordRead(userId), false);
  scoped('workspaces/credit-columns', reads.workspaceDirectory, (scope) => {
    const companyIds: string[] = DataService.getCompanies().map((company: { id?: string }) => company.id).filter((id: string | undefined): id is string => !!id);
    if (!companyIds.length) return Promise.resolve();
    const { name, load } = workspaceCreditColumnsRead(companyIds);
    return readInBackground(scope, name, load, { maxAgeMs: MORE_REWARM_AFTER_MS });
  });
  read(reads.profile, fieldAccessRead);
  read(reads.payroll, payrollOverviewRead, false);
  scoped('invoices/inbound', reads.purchases, (scope, id) => warmInvoiceRegister(scope, id, 'inbound', MORE_REWARM_AFTER_MS));
  scoped('invoices/outbound', reads.salesInvoices, (scope, id) => warmInvoiceRegister(scope, id, 'outbound', MORE_REWARM_AFTER_MS));
  read(reads.outreach, allProspectsFirstPageRead);
  outreachRead(templatesRead);
  outreachRead(settingsRead);
  outreachRead(suppressionsRead);
  scoped('email/shared-inbox', reads.sharedInbox, (scope) => warmLastSharedInbox(scope, MORE_REWARM_AFTER_MS));
  return tasks;
}
